import { createStore, set, get, keys, del } from "idb-keyval";
import { ViState } from "../contexts/ViContext";
import { ViImageData } from "../models";
import { ViIDBReadError, ViIDBWriteError } from "../models/Errors";
const viImageStore = createStore("Images", "Images");
const viProcessingStore = createStore("Processings", "Processings");
const activeStoredProcessingVersions: boolean[] = [false, false, false, true];

//TODO: clean users IDB in background

export async function storeImagesWithHash(imageData: ViImageData[]) {
  const existingKeys = await keys(viImageStore);
  for (const image of imageData) {
    if (!(image.id in existingKeys)) {
      await set(image.id, image.data, viImageStore);
    }
  }
}

export const storeViProcessingState = async (
  viState: ViState,
  caseId?: string,
  processingId?: string
): Promise<boolean> => {
  try {
    const imageData = viState.images?.map(({ id, data }) => ({ id, data })) || [];
    const trimmedImageData = viState.images?.map(({ data: _, ...image }) => image); //removing the base64 encoded image data since it's stored elswhere
    const viProcessingStateToStore: ViStoredProcessing = {
      ...(caseId && processingId ? { caseId, processingId } : {}),
      viState: {
        ...viState,
        images: trimmedImageData,
      },
      lastWrittenAt: Date.now(),
      version: 3,
    };
    const id = getProcessingStoreId(caseId, processingId);
    await set(id, viProcessingStateToStore, viProcessingStore);
    if (imageData?.length) storeImages(imageData);
  } catch (e) {
    const error = new ViIDBWriteError({ caughtError: e as any });
    throw error;
  }
  return true;
};

const storeImages = (images: { id: string; data: string }[]) => {
  try {
    const storedImageIds = keys(viImageStore);
    const promises = images.map(async ({ id, data }) => {
      if (!(id in storedImageIds)) return set(id, data, viImageStore);
      return false;
    });
    return Promise.all(promises);
  } catch (e) {
    const error = new ViIDBWriteError({ caughtError: e as Error });
    throw error;
  }
};

export const loadViProcessingState = async (
  caseId: string,
  processingId?: string
): Promise<ViState | undefined> => {
  try {
    const id = getProcessingStoreId(caseId, processingId);
    const loadedEntry: ViStoredProcessing | undefined = await get(id, viProcessingStore);
    const { viState, version } = loadedEntry || {};
    if (viState && version && activeStoredProcessingVersions[version]) {
      const images = viState.images;
      if (images?.length) {
        const loadedImages = images.map(async (image) => {
          const data: string | undefined = await get(image.id, viImageStore);
          if (!data) throw new ViIDBReadError("Unable to load image data.");
          return {
            ...image,
            data,
          };
        });
        return { ...viState, images: await Promise.all(loadedImages) };
      }
      return { ...viState, images: [] };
    }
    del(id, viProcessingStore);
    return undefined;
  } catch (e) {
    const error = new ViIDBReadError({ caughtError: e });
    throw error;
  }
};

const getProcessingStoreId = (caseId?: string, processingId?: string) =>
  caseId && processingId ? `${caseId}-${processingId}` : "INFERENCE";

export default Storage;

export interface ViStoredProcessing {
  caseId?: string;
  processingId?: string;
  viState: ViStoredState;
  lastWrittenAt: number;
  version: number;
}

export interface ViStoredState extends Omit<ViState, "images"> {
  id?: string;
  images?: Omit<ViImageData, "data">[];
}
